import { z } from "zod";

// Base schemas
export const EvaluationOptionSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  name: z.string(),
  description: z.string(),
  color_tag: z.string().optional(),
  color_text: z.string().optional(),
});

export const TemplateTaskSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  name: z.string(),
  description: z.string().optional(),
  tags: z.array(z.string()).optional(),
});

export const TaskPrioritySchema = z.enum(["low", "medium", "high", "critical"]);

export const TaskSchema = z.object({
  id: z.string().uuid(),
  template_task: TemplateTaskSchema,
  template_task_id: z.string().uuid().optional(),
  priority: TaskPrioritySchema,
  priority_display: z.string(),
  source_type: z.enum(["import", "manual", "ai"]),
  source_object_type: z.string(),
  source_object_id: z.number(),
  ignored_at: z.string().nullable(),
  ignored_by: z.number().nullable(),
  created: z.string(),
  modified: z.string(),
});

export const TaskEvaluationSchema = z.object({
  id: z.string().uuid(),
  employee_id: z.number(),
  template_task_id: z.string().uuid(),
  task_id: z.string().uuid().nullable(),
  evaluation_option: EvaluationOptionSchema.nullable(),
  evaluation_option_id: z.string().uuid().optional(),
  evaluated_at: z.string().datetime(),
  evaluator_user_id: z.number(),
  evaluator_user: z.object({
    username: z.string(),
    first_name: z.string().optional(),
    last_name: z.string().optional(),
  }),
});

// Input schemas for mutations
export const CreateTaskInputSchema = z.object({
  template_task_id: z.string().uuid().optional(),
  template_task_name: z.string().optional(), // For creating new template
  priority: TaskPrioritySchema,
});

export const UpdateTaskInputSchema = CreateTaskInputSchema.partial();

export const CreateEvaluationInputSchema = z.object({
  task_id: z.string().uuid().nullable(),
  template_task_id: z.string().uuid(),
  evaluation_option_id: z.string().uuid().nullable(),
});

// Response schemas
export const PaginatedResponseSchema = <T extends z.ZodType>(itemSchema: T) =>
  z.object({
    // ... other fields
    count: z.number().optional(),
    next: z.string().nullable(),
    previous: z.string().nullable(),
    results: z.array(itemSchema),
  });

export const TemplatesUsedSchema = z.array(
  z.tuple([
    z.string().uuid(), // first element: UUID string
    z.boolean(), // second element: boolean
  ]),
);
// Type exports
export type EvaluationOption = z.infer<typeof EvaluationOptionSchema>;
export type TemplateTask = z.infer<typeof TemplateTaskSchema>;
export type Task = z.infer<typeof TaskSchema>;
export type TaskEvaluation = z.infer<typeof TaskEvaluationSchema>;
export type CreateTaskInput = z.infer<typeof CreateTaskInputSchema>;
export type UpdateTaskInput = z.infer<typeof UpdateTaskInputSchema>;
export type CreateEvaluationInput = z.infer<typeof CreateEvaluationInputSchema>;
export type TemplatesUsed = z.infer<typeof TemplatesUsedSchema>;
